import { Engine } from "excalibur";
import { useState, useEffect } from "react";
import { MyLevel } from "../level";
import { DebugUrlStorage, SavedDebugUrl } from "../utils/debugUrlStorage";

type DebugProps = {
  game: Engine;
};

const DebugPane: React.FC<DebugProps> = ({ game }) => {
  const [saveButtonText, setSaveButtonText] = useState("Save Current State");
  const [savedUrls, setSavedUrls] = useState<SavedDebugUrl[]>([]);
  const [copiedUrlId, setCopiedUrlId] = useState<string | null>(null);

  useEffect(() => {
    // Load saved URLs on component mount
    setSavedUrls(DebugUrlStorage.getSavedUrls());
  }, []);

  const handleSaveCurrentState = async () => {
    const level = game.currentScene as MyLevel;
    if (level && level.generateShareableUrl) {
      try {
        const url = await level.generateShareableUrl();
        const turnCount = level.gameState.turnCount;
        const timestamp = formatTimestamp(new Date());
        const defaultLabel = `Turn ${turnCount} -- ${timestamp}`;

        DebugUrlStorage.saveUrl(url, defaultLabel);
        setSavedUrls(DebugUrlStorage.getSavedUrls());
        setSaveButtonText("Saved!");
        setTimeout(() => setSaveButtonText("Save Current State"), 2000);
      } catch (error) {
        console.error("Failed to save game state URL:", error);
      }
    }
  };

  const handleCopyUrl = async (url: string, id: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrlId(id);
      setTimeout(() => setCopiedUrlId(null), 2000);
    } catch (error) {
      console.error("Failed to copy URL:", error);
    }
  };

  const handleEditLabel = (id: string, currentLabel: string | undefined) => {
    const currentDisplay =
      currentLabel ||
      formatTimestamp(
        savedUrls.find((url) => url.id === id)?.timestamp || new Date(),
      );
    const newLabel = prompt(
      "Enter a custom label for this save:",
      currentDisplay,
    );

    if (newLabel !== null && newLabel.trim() !== "") {
      DebugUrlStorage.updateLabel(id, newLabel.trim());
      setSavedUrls(DebugUrlStorage.getSavedUrls());
    }
  };

  const handleDeleteUrl = (id: string) => {
    DebugUrlStorage.deleteUrl(id);
    setSavedUrls(DebugUrlStorage.getSavedUrls());
  };

  const handleLoadUrl = (url: string) => {
    // Extract the hash from the URL and update location
    const urlObj = new URL(url);
    if (urlObj.hash) {
      // If we have a hash, just update the hash (triggers hashchange event)
      window.location.hash = urlObj.hash;
    } else {
      // Fallback for old URLs or full navigation
      window.location.href = url;
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  return (
    <div className="relative overflow-visible">
      <button
        onClick={handleSaveCurrentState}
        className="px-4 py-2 mb-4 bg-green-500 hover:bg-green-600 text-white border-none rounded cursor-pointer transition-colors"
      >
        {saveButtonText}
      </button>

      {savedUrls.length > 0 && (
        <div className="mb-4 relative overflow-visible">
          <h4 className="mb-2 text-sm font-bold text-gray-800">
            Saved Game States
          </h4>
          <div className="max-h-[300px] overflow-y-auto overflow-x-visible relative">
            {savedUrls.map((savedUrl) => (
              <div
                key={savedUrl.id}
                className="group relative px-2 py-1.5 mb-1 bg-gray-100 hover:bg-gray-200 rounded text-xs text-black cursor-pointer transition-colors"
                onClick={() => handleCopyUrl(savedUrl.url, savedUrl.id)}
              >
                <span className="block w-full select-none pr-2">
                  {copiedUrlId === savedUrl.id
                    ? "Copied!"
                    : savedUrl.label || formatTimestamp(savedUrl.timestamp)}
                </span>
                <div className="absolute top-1 right-1">
                  <div className="flex gap-1 transition-opacity duration-200 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditLabel(savedUrl.id, savedUrl.label);
                      }}
                      className="px-2 py-0.5 text-[10px] bg-blue-500 hover:bg-blue-600 text-white border-none rounded cursor-pointer transition-colors"
                      title="Edit label"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLoadUrl(savedUrl.url);
                      }}
                      className="px-2 py-0.5 text-[10px] bg-green-500 hover:bg-green-600 text-white border-none rounded cursor-pointer transition-colors whitespace-nowrap"
                    >
                      Load
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteUrl(savedUrl.id);
                      }}
                      className="px-2 py-0.5 text-[10px] bg-red-500 hover:bg-red-600 text-white border-none rounded cursor-pointer transition-colors"
                    >
                      X
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-[12px] text-gray-200 mt-1 text-center">
            Click box to copy, Load to replace current game.
          </div>
        </div>
      )}
    </div>
  );
};

export default DebugPane;
